import { useCallback, useEffect, useState } from 'react';

import { LAYER_NORM_CUSTOM, LAYER_NORM_NONE } from '../../../constants/layers.ts';
import { usePedestrianLayerTypeEnabled } from '../../../hooks/useIdlc1129Features';
import { mapModelLayersToSettings } from '../../../utils/layerUtils';
import type { PhotometricTarget } from '../../../utils/photometricTargets';
import { ModelLayer } from '../../model-context/ModelContext';
import { LAYER_TYPES, LayerSetting } from '../types';

export const useLayerSettings = (modelLayers: ModelLayer[]) => {
  const pedestrianLayerType = usePedestrianLayerTypeEnabled();
  const getInitialLayerSettings = useCallback(
    (layers: ModelLayer[]): LayerSetting[] => mapModelLayersToSettings(layers, { pedestrian: pedestrianLayerType }),
    [pedestrianLayerType],
  );

  const [layerSettings, setLayerSettings] = useState<LayerSetting[]>(() => getInitialLayerSettings(modelLayers));

  useEffect(() => {
    setLayerSettings(getInitialLayerSettings(modelLayers));
  }, [getInitialLayerSettings, modelLayers]);

  const handleToggleChange = (layerName: string, field: 'illuminated' | 'canHoldLight', value: boolean) => {
    setLayerSettings((prev) =>
      prev.map((layer) => {
        if (layer.threeJsName === layerName) {
          // If turning off illumination, set norm to LAYER_NORM_NONE
          if (field === 'illuminated' && !value) {
            return { ...layer, [field]: value, norm: LAYER_NORM_NONE, customTargets: null };
          }
          return { ...layer, [field]: value };
        }
        return layer;
      }),
    );
  };

  const handleNormChange = (layerName: string, value: string) => {
    setLayerSettings((prev) =>
      prev.map((layer) => (layer.threeJsName === layerName ? { ...layer, norm: value, customTargets: null } : layer)),
    );
  };

  const handleCustomTargets = (layerName: string, targets: PhotometricTarget[]) => {
    setLayerSettings((prev) =>
      prev.map((layer) =>
        layer.threeJsName === layerName
          ? targets.length
            ? { ...layer, norm: LAYER_NORM_CUSTOM, customTargets: targets, illuminated: true }
            : { ...layer, norm: LAYER_NORM_NONE, customTargets: null }
          : layer,
      ),
    );
  };

  const handleTypeChange = (layerName: string, type: keyof typeof LAYER_TYPES) => {
    setLayerSettings((prev) =>
      prev.map((layer) =>
        layer.threeJsName === layerName
          ? {
              ...layer,
              type,
              color: type !== 'Unspecified' ? LAYER_TYPES[type] : layer.color,
              illuminated: layer.illuminated,
              canHoldLight: layer.canHoldLight,
              norm: layer.norm,
            }
          : layer,
      ),
    );
  };

  return {
    layerSettings,
    handleToggleChange,
    handleNormChange,
    handleCustomTargets,
    handleTypeChange,
  };
};
